import { useBrainStore } from '@/state/brainStore';
import { useSessionStore } from '@/state/sessionStore';
import { dispatchInboundCall, startOutboundCall } from '@/domain/callFlow';
import { customerName, simTimeFull } from '@/utils/format';
import { PhoneIncoming, PhoneOutgoing, History } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { ConversationEntry, CustomerRecord } from '@/domain/types';

interface CallLogRow {
  customer: CustomerRecord;
  entry: ConversationEntry;
}

export default function CallingWorkspace() {
  const navigate = useNavigate();
  const customers = useBrainStore((s) => s.customers);
  const studios = useBrainStore((s) => s.studios);
  const assignedStudios = useSessionStore((s) => s.assignedStudios);
  const activeStudio = useSessionStore((s) => s.activeStudio) ?? assignedStudios[0];

  const missed = Object.values(customers).filter(
    (c) =>
      c.leadStage === 'deposit-pending' ||
      c.conversation.some((m) => m.channel === 'voice' && m.direction === 'in'),
  );

  const callLog: CallLogRow[] = Object.values(customers)
    .flatMap((c) => c.conversation.filter((e) => e.channel === 'voice').map((entry) => ({ customer: c, entry })))
    .sort((a, b) => new Date(b.entry.timestamp).getTime() - new Date(a.entry.timestamp).getTime())
    .slice(0, 40);

  return (
    <section className="px-10 py-8" data-testid="calling-workspace">
      <header className="max-w-5xl">
        <p className="label-small-caps text-gold-bright">PRD §12.7 / §13.1</p>
        <h1 className="heading-display text-3xl text-cream mt-2">Call history</h1>
        <p className="font-serif text-cream-soft mt-3">
          {studios[activeStudio ?? '']?.name ??
            activeStudio?.replace('-', ' ') ??
            'No studio context'}{' '}
          · {studios[activeStudio ?? '']?.brandedNumber ?? '+61 unknown'}
        </p>
        <p className="font-serif text-cream-soft mt-2 max-w-xl">
          Active calls live inside the customer record. This screen handles missed-call
          recovery and the rolling log.
        </p>
      </header>

      <div className="mt-8 grid grid-cols-12 gap-8 max-w-6xl">
        <section className="col-span-7">
          <h2 className="heading-serif text-lg text-cream mb-4">Missed call recovery</h2>
          <ol className="space-y-3">
            {missed.length === 0 ? (
              <li className="text-sm text-cream-soft italic">No missed calls outstanding.</li>
            ) : null}
            {missed.map((c) => (
              <li
                key={c.id}
                data-testid={`missed-call-${c.id}`}
                className="panel p-4 flex items-center justify-between"
              >
                <div>
                  <p className="font-inter text-cream">{customerName(c)}</p>
                  <p className="font-inter text-[11px] text-cream-soft mt-1 uppercase tracking-widest">
                    {c.phone} · {c.suburb}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    data-testid={`call-now-${c.id}`}
                    onClick={() => {
                      startOutboundCall({ customerId: c.id, studioId: c.primaryStudioId });
                      navigate(`/customer/${c.id}`);
                    }}
                    className="btn-primary text-xs"
                  >
                    <PhoneOutgoing size={12} /> Call
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(`/customer/${c.id}`)}
                    className="btn-ghost text-xs"
                  >
                    Record
                  </button>
                </div>
              </li>
            ))}
          </ol>

          <h2 className="heading-serif text-lg text-cream mt-10 mb-4 flex items-center gap-2">
            <History size={16} className="text-gold-bright" /> Recent calls
          </h2>
          <ol className="space-y-2" data-testid="call-log">
            {callLog.length === 0 ? (
              <li className="text-sm text-cream-soft italic">No calls logged yet.</li>
            ) : null}
            {callLog.map(({ customer, entry }) => (
              <li
                key={entry.id}
                data-testid={`call-log-${entry.id}`}
                className="panel-dark p-3 flex items-center justify-between gap-3"
              >
                <button
                  type="button"
                  onClick={() => navigate(`/customer/${customer.id}`)}
                  className="flex-1 min-w-0 text-left"
                >
                  <p className="font-inter text-cream truncate">{customerName(customer)}</p>
                  <p className="font-serif text-sm text-cream-soft mt-0.5 truncate">
                    {entry.authorName ?? entry.authorRole}: {entry.body}
                  </p>
                </button>
                <span className="font-inter text-[10px] text-cream-faint uppercase tracking-widest shrink-0">
                  {simTimeFull(entry.timestamp)}
                </span>
              </li>
            ))}
          </ol>
        </section>

        <aside className="col-span-5">
          <h2 className="heading-serif text-lg text-cream mb-4">Simulate inbound</h2>
          <p className="text-sm text-cream-soft mb-4">
            Trigger an inbound ringing screen-pop without waiting for the canonical Monday
            schedule.
          </p>
          <ul className="space-y-2">
            {Object.values(customers)
              .slice(0, 5)
              .map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() =>
                      dispatchInboundCall({
                        customerId: c.id,
                        studioId: c.primaryStudioId,
                      })
                    }
                    className="w-full text-left panel-dark p-3 hover:border-gold-bright/60 transition flex items-center gap-3"
                  >
                    <PhoneIncoming size={14} className="text-gold-bright" />
                    <span className="text-sm">{customerName(c)}</span>
                    <span className="ml-auto font-inter text-[10px] text-cream-soft uppercase tracking-widest">
                      {c.primaryStudioId.replace('-', ' ')}
                    </span>
                  </button>
                </li>
              ))}
          </ul>
        </aside>
      </div>
    </section>
  );
}
